/**
 * Day by day, read for a person (DEC-28 C3).
 *
 * The service sends each day and the median of the four same weekdays before
 * it. This file only says how far a day is from that median, and in what
 * words: the comparison is the service's, the wording is the page's.
 */

import type { DailyFigures, DailyRow } from '../api'

export type Metric = 'charges_per_charge_point' | 'starts' | 'asks' | 'charge_points' | 'asks_per_charge'

export const METRICS: { key: Metric; label: string; title: string; ratio: boolean }[] = [
  { key: 'charges_per_charge_point', label: 'Charges per charge point', title: 'Charges started per active charge point', ratio: true },
  { key: 'starts', label: 'Charges started', title: 'Charges started per day', ratio: false },
  { key: 'asks', label: 'Asks', title: 'Asks per day', ratio: false },
  { key: 'charge_points', label: 'Charge points', title: 'Active charge points per day', ratio: false },
  { key: 'asks_per_charge', label: 'Asks per charge', title: 'Asks per charge started', ratio: true },
]

/** Percent from the baseline, whole, or null when there is nothing to compare. */
export function deltaPercent(value: number | null, base: number | null | undefined): number | null {
  if (value === null || base === null || base === undefined || base === 0) return null
  return Math.round(((value - base) * 100) / base)
}

/** The delta as the table writes it: a sign, or a dash for nothing to compare. */
export function deltaText(delta: number | null): string {
  if (delta === null) return 'nothing to compare'
  return `${delta > 0 ? '+' : ''}${delta}%`
}

/** Twenty per cent either way is worth a look; less is the week's noise. */
export function deltaTone(delta: number | null): 'up' | 'down' | 'flat' {
  if (delta === null) return 'flat'
  if (delta >= 20) return 'up'
  if (delta <= -20) return 'down'
  return 'flat'
}

export function valueText(value: number | null, ratio: boolean): string {
  if (value === null) return 'nothing to compare'
  return ratio ? value.toFixed(2) : value.toLocaleString('en-GB')
}

export function metricOf(figures: DailyFigures | null | undefined, metric: Metric): number | null {
  if (!figures) return null
  return figures[metric]
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "Fri 23 Oct". Three letters for every month: en-GB writes September as
 *  "Sept", and a table whose one month is four letters wide reads as a typo. */
export function dayLabel(row: Pick<DailyRow, 'day' | 'weekday'>): string {
  const [, month, day] = row.day.split('-')
  return `${row.weekday} ${day} ${MONTHS[Number(month) - 1]}`
}

/** The newest day that is over, which is the one a headline compares. */
export function lastWholeDay(days: DailyRow[]): DailyRow | null {
  for (let i = days.length - 1; i >= 0; i -= 1) {
    if (!days[i].partial) return days[i]
  }
  return null
}

/** The days as CSV, for a spreadsheet. Counts only, no token anywhere. */
export function toCsv(days: DailyRow[]): string {
  const columns: (keyof DailyFigures)[] = [
    'asks', 'approved', 'unknown', 'blocked', 'starts', 'stops', 'charge_points', 'tokens',
    'charges_per_charge_point', 'asks_per_charge',
  ]
  const header = ['day', 'weekday', 'partial', ...columns, ...columns.map((c) => `baseline_${c}`)]
  const lines = days.map((row) =>
    [
      row.day, row.weekday, String(row.partial),
      ...columns.map((c) => String(row[c] ?? '')),
      ...columns.map((c) => String(row.baseline?.[c] ?? '')),
    ].join(';'),
  )
  return [header.join(';'), ...lines].join('\n') + '\n'
}
